/**
 * AdminFAQCrud — full CRUD management for FAQ items stored in Firestore.
 *
 * Features:
 *  - List all FAQ items with publish toggle, edit, delete, and reorder (↑ / ↓)
 *  - Create / edit inline form
 *  - Embed code panel: copy the shortcode to paste into any CMS page body
 */

import { useEffect, useState, useCallback } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Copy,
  Check,
  ChevronUp,
  ChevronDown,
  Eye,
  EyeOff,
  HelpCircle,
  GripVertical,
  X,
} from 'lucide-react';
import {
  getAllFAQItems,
  createFAQItem,
  updateFAQItem,
  deleteFAQItem,
  reorderFAQItems,
  FAQ_EMBED_CODE,
  type FAQItem,
} from '@/lib/cms';

interface AdminFAQCrudProps {
  currentUserId: string;
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
  confirmAction?: (title: string, message: string, action: () => void) => void;
  /** Optional action (e.g. Help & guides) to show in the title row. */
  titleAction?: React.ReactNode;
}

interface FormState {
  question: string;
  answer: string;
  isPublished: boolean;
}

const EMPTY_FORM: FormState = { question: '', answer: '', isPublished: true };

export function AdminFAQCrud({
  currentUserId,
  titleAction,
  showNotify,
  confirmAction,
}: AdminFAQCrudProps): JSX.Element {
  const [items, setItems] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form state
  const [editingId, setEditingId] = useState<string | null>(null); // null = new
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<FormState>>({});

  // Embed code copy state
  const [copied, setCopied] = useState(false);

  const notify = useCallback(
    (msg: string, type: 'success' | 'error' | 'info' = 'info') => {
      showNotify?.(msg, type);
    },
    [showNotify]
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllFAQItems();
      setItems(data);
    } catch (err) {
      console.error('[AdminFAQCrud] Failed to load FAQ items:', err);
      notify('Failed to load FAQ items. Please refresh.', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  // ---- Form helpers -------------------------------------------------------

  function openNew() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowForm(true);
  }

  function openEdit(item: FAQItem) {
    setEditingId(item.id);
    setForm({ question: item.question, answer: item.answer, isPublished: item.isPublished });
    setFormErrors({});
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  }

  function validateForm(): boolean {
    const errors: Partial<FormState> = {};
    if (!form.question.trim()) errors.question = 'Question is required';
    if (!form.answer.trim()) errors.answer = 'Answer is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editingId) {
        await updateFAQItem(editingId, form, currentUserId);
        notify('FAQ item updated successfully', 'success');
      } else {
        const nextOrder = items.length > 0 ? Math.max(...items.map((i) => i.order)) + 1 : 0;
        await createFAQItem({ ...form, order: nextOrder }, currentUserId);
        notify('FAQ item created successfully', 'success');
      }
      closeForm();
      await loadItems();
    } catch (err) {
      console.error('[AdminFAQCrud] Failed to save FAQ item:', err);
      notify('Failed to save FAQ item. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  // ---- Delete -------------------------------------------------------------

  function handleDelete(item: FAQItem) {
    const doDelete = async () => {
      try {
        await deleteFAQItem(item.id);
        notify('FAQ item deleted', 'success');
        await loadItems();
      } catch (err) {
        console.error('[AdminFAQCrud] Failed to delete FAQ item:', err);
        notify('Failed to delete FAQ item.', 'error');
      }
    };
    if (confirmAction) {
      confirmAction(
        'Delete FAQ Item',
        `Are you sure you want to delete "${item.question}"? This cannot be undone.`,
        () => void doDelete()
      );
    } else {
      void doDelete();
    }
  }

  // ---- Publish toggle -----------------------------------------------------

  async function handleTogglePublish(item: FAQItem) {
    try {
      await updateFAQItem(item.id, { isPublished: !item.isPublished }, currentUserId);
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, isPublished: !i.isPublished } : i))
      );
    } catch (err) {
      console.error('[AdminFAQCrud] Failed to toggle FAQ item publish state:', err);
      notify('Failed to update publish state.', 'error');
    }
  }

  // ---- Reorder ------------------------------------------------------------

  async function handleMove(index: number, direction: 'up' | 'down') {
    const newItems = [...items];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newItems.length) return;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    setItems(newItems);
    try {
      await reorderFAQItems(
        newItems.map((i) => i.id),
        currentUserId
      );
    } catch (err) {
      console.error('[AdminFAQCrud] Failed to reorder FAQ items:', err);
      notify('Failed to save new order. Please reload.', 'error');
      await loadItems();
    }
  }

  // ---- Embed code copy ----------------------------------------------------

  async function handleCopyEmbed() {
    try {
      await navigator.clipboard.writeText(FAQ_EMBED_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      notify('Could not copy to clipboard. Please copy manually.', 'error');
    }
  }

  // ---- Render -------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <HelpCircle size={22} className="text-blue-600" aria-hidden="true" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">FAQ Management</h1>
            <p className="text-sm text-gray-500">
              Create and manage FAQ items. Publish the embed code to any CMS page.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {titleAction}
          <button
            onClick={openNew}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus size={16} aria-hidden="true" />
            Add FAQ Item
          </button>
        </div>
      </div>

      {/* Embed code panel */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 sm:p-5">
        <p className="text-sm font-semibold text-blue-800 mb-1">Embed Code</p>
        <p className="text-xs text-blue-600 mb-3">
          Copy this shortcode and paste it anywhere inside a CMS page body. The FAQ accordion will
          render automatically with all published items.
        </p>
        <div className="flex items-stretch gap-2">
          <code className="flex-1 block bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-700 select-all overflow-x-auto whitespace-nowrap">
            {FAQ_EMBED_CODE}
          </code>
          <button
            onClick={() => void handleCopyEmbed()}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shrink-0 ${
              copied
                ? 'bg-green-50 border-green-300 text-green-700'
                : 'bg-white border-blue-200 text-blue-700 hover:bg-blue-100'
            }`}
            aria-label="Copy embed code"
          >
            {copied ? (
              <>
                <Check size={14} aria-hidden="true" /> Copied
              </>
            ) : (
              <>
                <Copy size={14} aria-hidden="true" /> Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* Inline create / edit form */}
      {showForm && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-800">
              {editingId ? 'Edit FAQ Item' : 'New FAQ Item'}
            </h2>
            <button
              onClick={closeForm}
              className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
              aria-label="Close form"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          {/* Question */}
          <div>
            <label htmlFor="faq-question" className="block text-sm font-medium text-gray-700 mb-1">
              Question <span className="text-red-500">*</span>
            </label>
            <input
              id="faq-question"
              type="text"
              value={form.question}
              onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
              placeholder="e.g. How long does it take to secure meetings?"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.question ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
            />
            {formErrors.question && (
              <p className="mt-1 text-xs text-red-600">{formErrors.question}</p>
            )}
          </div>

          {/* Answer */}
          <div>
            <label htmlFor="faq-answer" className="block text-sm font-medium text-gray-700 mb-1">
              Answer <span className="text-red-500">*</span>
            </label>
            <textarea
              id="faq-answer"
              rows={4}
              value={form.answer}
              onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
              placeholder="Provide a clear and concise answer…"
              className={`w-full px-3 py-2 text-sm border rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                formErrors.answer ? 'border-red-400 bg-red-50' : 'border-gray-300'
              }`}
            />
            {formErrors.answer && (
              <p className="mt-1 text-xs text-red-600">{formErrors.answer}</p>
            )}
          </div>

          {/* Publish toggle */}
          <label className="flex items-center gap-2.5 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Published (visible on site)</span>
          </label>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => void handleSave()}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Item'}
            </button>
            <button
              onClick={closeForm}
              className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* FAQ list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 flex flex-col items-center justify-center py-14 px-4 text-center">
          <HelpCircle size={40} className="text-gray-300 mb-3" aria-hidden="true" />
          <p className="text-sm font-medium text-gray-500">No FAQ items yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Click "Add FAQ Item" to create your first entry.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wider px-1">
            {items.length} item{items.length !== 1 ? 's' : ''} — drag ↑ ↓ to reorder
          </p>
          {items.map((item, idx) => (
            <div
              key={item.id}
              className={`group flex items-start gap-3 rounded-xl border bg-white p-4 transition-shadow hover:shadow-sm ${
                item.isPublished ? 'border-gray-200' : 'border-gray-200 opacity-60'
              }`}
            >
              {/* Drag handle / order indicator */}
              <div className="flex flex-col items-center gap-0.5 pt-0.5 shrink-0">
                <GripVertical size={14} className="text-gray-300" aria-hidden="true" />
                <button
                  onClick={() => void handleMove(idx, 'up')}
                  disabled={idx === 0}
                  className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                  aria-label="Move up"
                >
                  <ChevronUp size={14} aria-hidden="true" />
                </button>
                <button
                  onClick={() => void handleMove(idx, 'down')}
                  disabled={idx === items.length - 1}
                  className="p-0.5 text-gray-300 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 rounded"
                  aria-label="Move down"
                >
                  <ChevronDown size={14} aria-hidden="true" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 leading-snug">{item.question}</p>
                <p className="mt-1 text-xs text-gray-500 line-clamp-2 leading-relaxed">
                  {item.answer}
                </p>
              </div>

              {/* Status badge */}
              <span
                className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  item.isPublished
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {item.isPublished ? 'Published' : 'Draft'}
              </span>

              {/* Actions */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => void handleTogglePublish(item)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={item.isPublished ? 'Unpublish' : 'Publish'}
                  title={item.isPublished ? 'Unpublish' : 'Publish'}
                >
                  {item.isPublished ? (
                    <EyeOff size={15} aria-hidden="true" />
                  ) : (
                    <Eye size={15} aria-hidden="true" />
                  )}
                </button>
                <button
                  onClick={() => openEdit(item)}
                  className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label="Edit FAQ item"
                  title="Edit"
                >
                  <Pencil size={15} aria-hidden="true" />
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  className="p-1.5 text-gray-400 hover:text-red-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-red-400"
                  aria-label="Delete FAQ item"
                  title="Delete"
                >
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
